import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useSelector } from "react-redux";
import { Route, Routes, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { NetworkErrorAlert } from "../../components/infoPopup/infoPopup";
import CustomLoader from "../../components/loader/loader";
import LinkedinCallback from "../../components/login/linkedin-callback";
import RequestAuthenticate from "../../components/login/requestAuthenticate";
import SuccessModal from "../../components/login/successModal";
import SubDomainRouter from "../../components/subDomainRouter/subDomainRouter";
import * as Actions from "../../constants/baseUrl";
import RootLayout from "../../layouts/RootLayout";
import { setCookie } from "../../utils/cookies";
import DynamicHomepageDetails from "../admin/dynamicHomepages/details";
import PageDetails from "../admin/pages/details";
import GroupAppointment from "../appointment/GroupAppointment";
import AutomateCampaign from "../automateCampaign/automateCampaign";
import BlogDetails from "../blog/details";
import Blogs from "../blog/getAll";
import ChatAI from "../chatAI/chatAI";
import Cohorts from "../cohorts/getAll";
import Contact from "../contact";
import Coordinator from "../coordinator/coordinator";
import Corporates from "../corporates/corporates";
import CourseDetails from "../courses/details";
import CourseListing from "../courses/getAll";
import DemoDetails from "../demos/details";
import Demos from "../demos/getAll";
import EventDetails from "../events/details";
import Events from "../events/getAll";
import Freelancers from "../freelancers/listing";
import Items from "../genericItems/listing";
import Home from "../home/Home";
import Meetups from "../meetups/meetups";
import Mentors from "../mentors/listing";
import RegisterNewsletter from "../newsletter/register";
import NRIStartups from "../NRIStartups/listing";
import Properties from "../property/getAll";
import PropertyEventDetails from "../propertyEvents/details";
import PropertyEvents from "../propertyEvents/getAll";
import RedirectToExternal from "../RedirectToExternal/RedirectToExternal";
import JSONEntityAddEdit from "../schemaEntity/add_edit";
import ServiceProvider from "../serviceProviders/listing";
import Speakers from "../speakers/speakers";
import Startups from "../startups/startup";
import ConfirmEmail from "./confirm-email/confirm-email";
import ResetPassword from "./reset-password";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { useAuthProviders } from "../../helper/auth/AuthProviderContext";

const AuthPage = () => {
  const navigate = useNavigate();

  const { googleClient } = useAuthProviders();

  const currentHost = window.location.host;
  const currentPathname = window.location.pathname;

  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [signupErrorMsg, setSignupErrorMsg] = useState("");

  const siteName = SubDomainRouter("title");
  const favicon = SubDomainRouter("favicon");
  const homePageRoute = SubDomainRouter("homePageRoute");

  const configStatus = useSelector((state) => state.hostSettings.status);
  const configData = useSelector((state) => state.hostSettings.value);
  const [configLoading, setConfigLoading] = useState(true);

  useEffect(() => {
    if (!(configData && configStatus !== "failed")) {
      return;
    }
    document.title = siteName ? siteName : String(currentHost).toUpperCase();

    if (currentPathname === "/") {
      if (homePageRoute === "/home") {
        setConfigLoading(false);
        return;
      } else if (
        !configData?.header?.navItem ||
        configData?.header?.navItem === "hide" ||
        homePageRoute !== "/"
      ) {
        navigate(homePageRoute);
      }
    }

    setConfigLoading(false);
  }, [
    configData,
    currentPathname,
    configData?.header?.navItem,
    homePageRoute,
    navigate,
    siteName,
  ]);

  useEffect(() => {
    if (!favicon) {
      return;
    }
    document.title = siteName || String(currentHost).toUpperCase();

    const updateFavicon = (favicon) => {
      const link =
        document.querySelector("link[rel~='icon']") ||
        document.createElement("link");
      link.type = "image/x-icon";
      link.rel = "shortcut icon";
      link.href = favicon;
      document.getElementsByTagName("head")[0].appendChild(link);
    };

    updateFavicon(favicon);
  }, [favicon, siteName]);

  const formRef = useRef(null);

  const handleSubmit = (event) => {
    if (loading) return;

    setLoading(true);
    setSignupErrorMsg("");
    event.preventDefault();
    if (mode) {
      const request = {
        email: event.currentTarget.email.value,
        password: event.currentTarget["password"].value,
        //?====== for prod + test ===========
        // username: `${e.currentTarget.email.value}_${
        //   currentHost === "localhost:3000" ? "localhost" : currentHost
        // }`,
      };
      axios
        .post(`${Actions.AUTH_URL}Auth/login`, request, {
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          },
        })
        .then((res) => {
          const data = res.data;
          if (
            data === "DOESNOTEXIST" ||
            data.authToken === null ||
            data.authToken === "" ||
            data.authToken === undefined
          ) {
            localStorage.setItem("isValidUser", false);
            toast.error("In valid credentials. Please try again.", {
              autoClose: 3000,
              hideProgressBar: true,
              draggable: true,
            });
          } else {
            localStorage.setItem("isValidUser", true);
            localStorage.setItem("userData", JSON.stringify(data));
            // Set token in cookie instead of localStorage
            setCookie("token", data.authToken, { expires: 7, secure: true });
            if (currentPathname?.includes("/login")) {
              window.location.href = "/";
            } else {
              window.location.reload();
            }
          }
          setLoading(false);
        })
        .catch((error) => {
          handleApiError(error);
          setLoading(false);
          toast.error("Something went wrong. Please try again later.", {
            autoClose: 3000,
            hideProgressBar: true,
            draggable: true,
          });
        });
    } else {
      const request = {
        email: event.currentTarget.newEmail.value,
        password: event.currentTarget.createpassword.value,
        role: event.currentTarget.role.value,
        phoneNumber: event.currentTarget.phone.value,
        whatsAppNumber: event.currentTarget.phone.value,
        firstName: event.currentTarget.firstname.value,
        lastName: event.currentTarget.lastname.value,
        //?====== for prod + test ===========
        // username: `${event.currentTarget.newEmail.value}`,
      };
      axios
        .post(`${Actions.AUTH_URL}Users`, request, {
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          },
        })
        .then((res) => {
          setShowSuccessModal(true);
          formRef.current.reset();
          setLoading(false);
        })
        .catch((error) => {
          handleApiError(error);
          setLoading(false);
          if (Array.isArray(error?.response?.data)) {
            toast.error(error?.response?.data?.[0]);
          } else {
            toast.error("Something went wrong. Please try again later.");
          }
        });
    }
  };

  const handleApiError = (error) => {
    if (error?.message === "Network Error") {
      NetworkErrorAlert(error?.message);
    }

    if (error?.response?.data) {
      // Use a regular expression to capture everything until the first full stop or line break
      if (Array.isArray(error?.response?.data)) {
        setSignupErrorMsg(error?.response?.data?.[0]);
      } else {
        const match = error?.response?.data?.match(
          /System\.ApplicationException: (.*?)(\n|$)/s
        );
        if (match && match.length > 1) {
          const errorMessage = match[1];
          setSignupErrorMsg(errorMessage.trim());
        } else {
          // If the error message is not in the expected format, log it for debugging purposes
          console.error("Unexpected error format:", error?.response.data);
        }
      }
    }

    setLoading(false);
  };

  return (
    <>
      <Helmet>
        <title>{siteName ? siteName : String(currentHost).toUpperCase()}</title>
      </Helmet>
      {/* If not authorized */}

      {configLoading && configStatus === "loading" ? (
        <CustomLoader height={"100vh"} className={"pt-0 mt-0 pb-0 mb-0"} />
      ) : (
        <GoogleOAuthProvider clientId={googleClient}>
          <Routes>
            <Route
              path="*"
              exact
              element={
                <div className={`app app--is-${mode ? "login" : "signup"}`}>
                  {loading ? (
                    <>
                      <span className="register-loader theme-text-2"> </span>
                      <span className="spinnerOverlay" />
                    </>
                  ) : null}
                  <RequestAuthenticate
                    mode={mode}
                    formRef={formRef}
                    signupErrorMsg={signupErrorMsg}
                    setMode={setMode}
                    setLoading={setLoading}
                    onSubmit={handleSubmit}
                    onChange={(e) => {}}
                  />
                </div>
              }
            />

            <Route path="/signin-linkedin" element={<LinkedinCallback />} />

            <Route path="/confirm-email" element={<ConfirmEmail />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            <Route element={<RootLayout />}>
              <Route
                path={homePageRoute === "/" ? "/" : "/home"}
                exact
                element={<Home />}
              />

              <Route path="/pages/details/:id" element={<PageDetails />} />

              <Route path="/corporates" element={<Corporates />} />
              <Route path="/contact" element={<Contact />} />

              <Route path="/courses" element={<CourseListing />} />
              <Route path="/courses/details/:id" element={<CourseDetails />} />

              <Route path="/blogs" element={<Blogs />} />
              <Route path="/blogs/details/:id" element={<BlogDetails />} />

              <Route path="/demos" element={<Demos />} />
              <Route path="/demos/details/:id" element={<DemoDetails />} />

              <Route path={"/events"} element={<Events />} />
              <Route path="/events/details/:id" element={<EventDetails />} />

              <Route path="/coordinator/join" element={<Coordinator />} />

              {/* PropertyEvent */}
              <Route
                path={"/propertyevent/items"}
                element={<PropertyEvents />}
              />
              <Route
                path="/propertyevent/items/details/:id"
                element={<PropertyEventDetails />}
              />
              <Route
                path="/homepages/details/:id"
                element={<DynamicHomepageDetails />}
              />

              <Route path={"/code-ai"} element={<ChatAI />} />
              <Route path={"/startup/items"} element={<Startups />} />
              <Route path="/meetups/items" element={<Meetups />} />
              <Route path="/speaker/items" element={<Speakers />} />
              <Route
                path="/service-provider/items"
                element={<ServiceProvider />}
              />
              <Route path={"/mentor/items"} element={<Mentors />} />
              <Route path={"/freelancer/items"} element={<Freelancers />} />
              <Route path={"/NRI-startup/items"} element={<NRIStartups />} />
              <Route path={"/property/items"} element={<Properties />} />
              <Route path="/cohort/items" element={<Cohorts />} />

              <Route path="/:item_type/items" element={<Items />} />

              <Route path="/newsletter" element={<RegisterNewsletter />} />
              <Route path="/appointment" element={<GroupAppointment />} />
              <Route path="/subscribe" element={<RegisterNewsletter />} />

              <Route
                path="/campaigns/:entityType"
                element={<AutomateCampaign />}
              />

              <Route
                path="/forms/:entityType/add"
                element={<JSONEntityAddEdit />}
              />
            </Route>

            {configData?.common?.dynamicUrls?.map((url, index) => {
              if (
                url?.key !== "/" &&
                url?.key !== "" &&
                url?.value !== "/" &&
                url?.value !== ""
              ) {
                return (
                  <Route
                    key={index}
                    path={`/${url.key}`}
                    element={<RedirectToExternal to={url.value} />}
                  />
                );
              }
              return null;
            })}
          </Routes>

          {/*  */}

          {showSuccessModal ? (
            <SuccessModal
              setMode={setMode}
              show={showSuccessModal}
              setshow={setShowSuccessModal}
              onHide={() => {
                setShowSuccessModal(false);
              }}
            />
          ) : null}
        </GoogleOAuthProvider>
      )}
    </>
  );
};
export default AuthPage;
